// src/views/AdminStatsView.jsx — Écran « Statistiques » de la destination
// € Argent (lot Statistiques 2026-09, refonte à DEUX MODÈLES — cadrage
// ROADMAP_FBZ_V3.md § LOT STATISTIQUES, décisions Dimitri 2026-08-28).
//
// DEUX MODÈLES SÉPARÉS, jamais mélangés dans un même chiffre :
//   1. CONCIERGERIE (logements des propriétaires, l1-l7) : modèle commission —
//      moteur `aggregateStats` sur un périmètre SANS les chambres sous-loc,
//      + bloc « Par propriétaire » (moteur KV facturation, calcPure +
//      calcDirectInternePure + mergeCalc, byte-identique à l'ancien Annuel).
//      Les deux moteurs coexistent MAIS couvrent désormais le même modèle
//      économique sur le même périmètre (la sous-loc n'y entre plus jamais).
//   2. SOUS-LOCATION (Maison Zen, lc1/lc2/lc3) : marge = CA des chambres −
//      loyer versé − charges portées (formule actée Dimitri). Moteur
//      `buildSousLocStats` → allocSousLocCharges / calcSousLocForLog, les MÊMES
//      fonctions que Compta (les écrans disent le même chiffre). Répartition
//      loyer/charges entre chambres : parts égales 1/N (actée Dimitri).
//
// Occupation (mesure 3 corrigée) : le dénominateur est PAR MOIS — un logement
// ne compte dans les nuits disponibles qu'à partir de son premier mois
// d'activité (nbLogementsByMk), et la plage par défaut = 12 derniers mois
// COMPLETS (le mois en cours, entamé, ne dilue plus le taux par défaut).
//
// LECTURE SEULE, CLIENT-ONLY (Option A) : aucun fetch DB nouveau — tout est
// dérivé des sources DÉJÀ en mémoire dans App.jsx (cfg/dataProjete/blocksByMonth).
// L'admin voit TOUT (pas de cloisonnement anti-IDOR). Deux filtres : plage
// libre « De … à … » + filtre logements par cases à cocher.
//
// ⚠️ EXPORTS PARTAGÉS AVEC L'ESPACE PROPRIO : StatsDashboard, LogementFilter,
// enumMonths, defaultRange, validateRange, RANGE_INPUT_STYLE sont consommés par
// ProprioStatsView — leur comportement ne DOIT pas changer (le proprio ne voit
// que SES logements conciergerie ; le modèle sous-loc ne le concerne pas).

import { useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart,
  Banknote,
  Wallet,
  CalendarRange,
  TrendingUp,
  BarChart3,
  Building2,
  Moon,
  CalendarOff,
  Users,
  CalendarDays,
  Tag,
  Percent,
  ReceiptText,
  Sparkles,
  KeyRound,
  Table as TableIcon,
  UsersRound,
  Download,
} from "lucide-react";
import { DS } from "../lib/ui";
import { fmtE, fmt2, MONTHS_SHORT, formatMois, propDisplayName } from "../lib/format";
import { calc as calcPure, calcDirectInternePure, mergeCalc, isSousLocLid } from "../lib/calc";
import { getAllLogsFlat } from "../lib/sousloc-helpers";
import { aggregateStats } from "../lib/stats-aggregate";
import { buildAdminStatsInputs } from "../lib/stats-admin-inputs";
import { buildSousLocStats } from "../lib/stats-sousloc";
import {
  SectionTitle,
  InfoDot,
  EmptyBox,
  STATS_CSS,
  SubLabel,
  Reveal,
  DonutCard,
  donutParts,
} from "./proprio/_proprioUi";

// ── Libellés d'aide (tooltips ⓘ) — repris à l'identique de ProprioStatsView. ──
const INFO = {
  hebergementsActifs: "Le nombre de logements pris en compte dans ces statistiques.",
  nuitsLouees: "Nombre de nuits réservées par des voyageurs payants sur la période.",
  // Le calcul réel est `nuitsDispo − nuitsLouees` (stats-aggregate.js) : ni les
  // nuits BLOQUÉES ni les nuits occupées SANS MONTANT n'en sont retirées. Dire
  // « encore libres » était donc faux — une nuit bloquée était annoncée comme
  // occupée (taux d'occupation) ET comme encore libre (ce KPI).
  nuitsRestantes:
    "Nuits disponibles moins nuits louées, sur la période. Ce n'est pas un stock re-vendable : les nuits bloquées (travaux, indisponibilité) et les nuits occupées sans montant restent comptées ici.",
  occupation: "Part des nuits qui ont été louées, sur l'ensemble des nuits de la période.",
  nbVoyageursMoy: "Nombre moyen de voyageurs par réservation.",
  nuitsMoyResa: "Durée moyenne d'un séjour, en nuits.",
  prixMoyenResa: "Montant brut moyen d'une réservation.",
  revPAR: "Revenu moyen par nuit disponible (qu'elle soit louée ou non).",
  // La taxe de séjour n'entre JAMAIS dans ce total :
  //   - OTA : resas-v2-source.js force `taxe = 0` (Airbnb comme Booking la
  //     collectent et la reversent eux-mêmes) et caBrut = total_amount ;
  //   - direct interne : stats-aggregate.js ajoute `dc.ca`, dont calc.js exclut
  //     explicitement la taxe (« montant SÉPARÉ à reverser : JAMAIS ajouté au CA »).
  // Elle est suivie à part, dans la carte « Taxe de séjour ».
  caBrut:
    "Montant total payé par les voyageurs : nuitées + ménage + commission plateforme. La taxe de séjour n'y est pas comptée (elle est suivie séparément).",
  commissionsOta: "Ce qu'Airbnb et Booking prélèvent sur les réservations.",
  revenusNets: "Ce que rapporte le bien une fois la commission de la plateforme déduite.",
  taxe:
    "Taxe de séjour collectée et reversée sur les réservations directes (sur Airbnb et Booking, la plateforme la collecte et la reverse elle-même).",
  menage: "Total des frais de ménage refacturés aux voyageurs sur la période.",
  checkinCheckout: "Nombre d'arrivées et de départs de voyageurs sur la période.",
  donutResas: "D'où viennent les réservations, par plateforme.",
  donutCa: "Quelle plateforme génère le plus de revenus.",
  proprio:
    "Répartition de la commission FBZ et du chiffre d'affaires par propriétaire (données de facturation KV).",
  // ── Sous-location (lot Statistiques) — textes = état réel du calcul. ──
  slCa: "Ce que les chambres ont rapporté sur la période (réservations et séjours directs).",
  slLoyer:
    "Le loyer versé au propriétaire, tel que saisi mois par mois dans Facturation > Sous-location.",
  slCharges:
    "Les charges variables payées par FBZ, telles que saisies mois par mois dans Facturation > Sous-location.",
  slMarge:
    "Revenus des chambres moins loyer versé moins charges portées. C'est ce qui reste réellement à FBZ.",
  slChambre:
    "Chaque chambre porte une part égale du loyer et des charges. Une chambre en négatif se loue moins bien que sa part ne coûte.",
};

// ── Palette catégorielle (hex EXACTS de la maquette — DS n'a ni violet ni amber). ──
const CAT = {
  sky: { bg: DS.cielSoft, fg: DS.marine }, // Volume
  violet: { bg: "#EEEDFE", fg: "#534AB7" }, // Séjour
  amber: { bg: "#FAEEDA", fg: "#854F0B" }, // Argent
  green: { bg: DS.successBg, fg: DS.successText }, // Opérations
  corail: { bg: DS.corailSoft, fg: DS.corailText }, // CA / proprio
};
const LEGEND = [
  ["Volume", "#87b8e6"],
  ["Séjour", "#534AB7"],
  ["Argent", "#EF9F27"],
  ["Opérations", "#1D9E75"],
];

const LOCAL_CSS = `
.fbz-astat-tiles { display:grid; grid-template-columns:repeat(5,1fr); gap:12px; }
.fbz-astat-card { transition:transform .2s ease, box-shadow .2s ease; }
.fbz-astat-card:hover { transform:translateY(-2px); box-shadow:0 7px 18px rgba(27,58,92,.09); }
@media (max-width:900px){ .fbz-astat-tiles{ grid-template-columns:repeat(3,1fr); } }
@media (max-width:560px){ .fbz-astat-tiles{ grid-template-columns:repeat(2,1fr); } }
`;

// Style DS des champs de plage (hauteur 40, radius 10, bordure #cdd5df).
export const RANGE_INPUT_STYLE = {
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "0.5px solid #cdd5df",
  background: "#fff",
  color: DS.marineDark,
  fontWeight: 600,
  fontSize: 13,
  fontFamily: DS.fontBody,
  cursor: "pointer",
};

// ── Plage libre « De … à … » → Array<mk "YYYY-MM"> (client-only). ──
// La granularité du moteur est le MOIS calendaire : la plage libre est découpée
// en mois calendaires INCLUS (convention validée Dimitri). Le guard borne
// l'énumération à 60 itérations = plage libre max 5 ans (borne UI cohérente).
const MAX_RANGE_MONTHS = 60;
export function enumMonths(from, to) {
  const out = [];
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  let y = fy;
  let m = fm;
  let guard = 0;
  while ((y < ty || (y === ty && m <= tm)) && guard < MAX_RANGE_MONTHS) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    guard += 1;
  }
  return out;
}
// Nombre de mois calendaires inclus dans [from, to] (négatif/0 si from > to).
function monthSpan(from, to) {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  return (ty - fy) * 12 + (tm - fm) + 1;
}
// mk "YYYY-MM" à un offset de `delta` mois du mois courant (0 = mois courant).
function currentMonthKey(delta = 0) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
// Défaut à l'ouverture : 12 derniers mois glissants (ancien défaut « 12 »).
// ⚠️ Export consommé par ProprioStatsView — ne pas changer (rendu proprio figé).
export function defaultRange() {
  return { from: currentMonthKey(-11), to: currentMonthKey(0) };
}
// Défaut ADMIN (mesure 3, lot Statistiques) : 12 derniers mois COMPLETS. Le
// mois en cours, entamé, comptait plein de nuits disponibles pas encore
// écoulées et diluait l'occupation — il reste sélectionnable à la main.
function defaultRangeAdmin() {
  return { from: currentMonthKey(-12), to: currentMonthKey(-1) };
}
// Retourne un message d'erreur (string) si la plage est invalide, sinon null.
export function validateRange(from, to) {
  if (!from || !to) return "Renseignez les deux dates.";
  if (from > to) return "La date de début doit précéder la fin.";
  if (monthSpan(from, to) > MAX_RANGE_MONTHS) return "La plage ne peut pas dépasser 5 ans.";
  return null;
}

// ── Formatteurs nullable ──
function fmt(v, suffix = "") {
  if (v == null) return "—";
  return `${v}${suffix}`;
}
function fmtEur(v) {
  return v == null ? "—" : fmtE(v);
}
function fmtPct(v) {
  return v == null ? "—" : `${fmt2(v)} %`;
}
// Euro compact pour l'axe du graphe (25k€ / 5k€ / 0).
function kEur(v) {
  if (!v) return "0";
  if (v >= 1000) return `${Math.round(v / 1000)}k€`;
  return `${Math.round(v)} €`;
}
// Plafond « joli » pour l'axe (1/2/5 × 10^n).
function niceCeil(v) {
  if (!v || v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}
// Initiales proprio pour l'avatar (2 premiers mots → "MME & M. POUPIN" = "M&").
function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// ── Sparkline aire remplie (SVG maison — maquette « dashboard vivant »). ──
function Sparkline({ values, stroke, fill }) {
  const v = (values || []).filter((x) => x != null);
  if (v.length < 2) return null;
  const max = Math.max(...v);
  const min = Math.min(...v, 0);
  const range = max - min || 1;
  const P = 4; // padding vertical
  const H = 28;
  const inner = H - P * 2;
  const pts = v.map((val, i) => {
    const x = (i / (v.length - 1)) * 100;
    const y = P + (1 - (val - min) / range) * inner;
    return [x, Number(y.toFixed(2))];
  });
  const line = pts.map((p) => `${p[0].toFixed(1)},${p[1]}`).join(" ");
  const area = `M${pts[0][0].toFixed(1)},${pts[0][1]} ${pts
    .slice(1)
    .map((p) => `L${p[0].toFixed(1)},${p[1]}`)
    .join(" ")} L100,${H} L0,${H} Z`;
  return (
    <svg
      viewBox={`0 0 100 ${H}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: 30, marginTop: 10, display: "block" }}
      aria-hidden="true"
    >
      <path d={area} fill={fill} opacity="0.1" />
      <polyline
        points={line}
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// Badge de tendance (dernier mois vs précédent, valeur RÉELLE). unit : '%' | 'pts'.
function TrendBadge({ values, unit }) {
  const v = (values || []).filter((x) => x != null);
  if (v.length < 2) return null;
  const prev = v[v.length - 2];
  const cur = v[v.length - 1];
  let up;
  let text;
  if (unit === "pts") {
    const d = Math.round((cur - prev) * 10) / 10;
    if (d === 0) return null;
    up = d > 0;
    text = `${up ? "+" : "−"}${Math.abs(d)} pts`;
  } else {
    if (!prev || prev <= 0) return null;
    const pct = Math.round(((cur - prev) / prev) * 100);
    if (pct === 0) return null;
    up = pct > 0;
    text = `${up ? "+" : "−"}${Math.abs(pct)}%`;
  }
  return (
    <span
      style={{
        marginLeft: "auto",
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: 7,
        background: up ? DS.successBg : DS.dangerBg,
        color: up ? DS.successText : DS.danger,
      }}
    >
      {up ? "▲" : "▼"}
      {text}
    </span>
  );
}

// ── Carte KPI hero (maquette : pastille + label + trend + valeur + sparkline). ──
function KpiCard({ icon: Icon, cat, label, value, info, series, spark, unit }) {
  return (
    <div
      className="fbz-astat-card"
      style={{
        background: DS.white,
        border: `0.5px solid ${DS.border}`,
        borderRadius: 14,
        padding: "16px 18px",
        boxShadow: "0 1px 3px rgba(33,83,135,.05)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: cat.bg,
            color: cat.fg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={16} strokeWidth={2} />
        </span>
        <span
          style={{
            fontSize: 11,
            letterSpacing: ".04em",
            fontWeight: 700,
            textTransform: "uppercase",
            color: DS.textMuted,
          }}
        >
          {label}
        </span>
        {info && <InfoDot text={info} />}
        <TrendBadge values={series} unit={unit} />
      </div>
      <div
        style={{
          fontFamily: DS.fontDisplay,
          fontWeight: 700,
          fontSize: 25,
          color: DS.marineDark,
          lineHeight: 1,
          marginTop: 13,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      <Sparkline values={series} stroke={spark.stroke} fill={spark.fill} />
    </div>
  );
}

// ── LE TABLEAU DE BORD (partagé admin ↔ proprio — refonte proprio lot ③,
// décision Dimitri 2026-08-22 : « reprends le composant de l'espace admin, tel
// quel »). Rendu PUR depuis un agrégat `stats` (aggregateStats — calculé côté
// client chez l'admin, servi par proprio_stats cloisonné chez le proprio) :
// KPIs héro, évolution du CA par plateforme, tuiles d'activité, donuts,
// détail mois par mois. AUCUNE donnée FBZ-only ici — la commission FBZ vit
// dans le bloc admin « Par propriétaire », HORS de ce composant.
export function StatsDashboard({ stats }) {
  const total = stats?.total || null;
  const kpis = stats?.kpis || null;
  const parMoisEntries = stats
    ? Object.entries(stats.parMois || {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    : [];
  // Séries mensuelles réelles (sparklines + tendances).
  const caSeries = parMoisEntries.map(([, m]) => m.caBrut);
  const netSeries = parMoisEntries.map(([, m]) => m.revenusNets);
  const occSeries = parMoisEntries.map(([, m]) => m.occupation);
  const revparSeries = parMoisEntries.map(([, m]) =>
    m.nuitsDispo ? Math.round((m.caBrut / m.nuitsDispo) * 100) / 100 : null,
  );
  if (!total || !kpis) return null;
  return (
    <>
      <style>{STATS_CSS}</style>
      <style>{LOCAL_CSS}</style>
          {/* ── VUE D'ENSEMBLE : 4 KPIs hero. ── */}
          <SubLabel>Vue d'ensemble</SubLabel>
          <div className="fbz-kpi-hero" style={{ marginBottom: 20 }}>
            <Reveal i={0}>
              <KpiCard
                icon={Banknote}
                cat={CAT.corail}
                label="CA brut"
                value={fmtEur(total.caBrut)}
                info={INFO.caBrut}
                series={caSeries}
                spark={{ stroke: "#E8632E", fill: "#E8632E" }}
                unit="%"
              />
            </Reveal>
            <Reveal i={1}>
              <KpiCard
                icon={Wallet}
                cat={CAT.green}
                label="Revenus nets"
                value={fmtEur(total.revenusNets)}
                info={INFO.revenusNets}
                series={netSeries}
                spark={{ stroke: "#1D9E75", fill: "#1D9E75" }}
                unit="%"
              />
            </Reveal>
            <Reveal i={2}>
              <KpiCard
                icon={CalendarRange}
                cat={CAT.sky}
                label="Occupation"
                value={fmtPct(total.occupation)}
                info={INFO.occupation}
                series={occSeries}
                spark={{ stroke: "#87b8e6", fill: "#3d5166" }}
                unit="pts"
              />
            </Reveal>
            <Reveal i={3}>
              <KpiCard
                icon={TrendingUp}
                cat={CAT.violet}
                label="RevPAR"
                value={fmtEur(kpis.revPAR)}
                info={INFO.revPAR}
                series={revparSeries}
                spark={{ stroke: "#534AB7", fill: "#534AB7" }}
                unit="%"
              />
            </Reveal>
          </div>

          {/* ── Évolution du CA : barres empilées par plateforme + axe. ── */}
          {parMoisEntries.length > 0 &&
            (() => {
              const bars = parMoisEntries.map(([mk, m]) => {
                const [, mmn] = mk.split("-");
                const a = m.parPlateforme?.airbnb?.caBrut || 0;
                const b = m.parPlateforme?.booking?.caBrut || 0;
                const d = m.parPlateforme?.direct?.caBrut || 0;
                return {
                  mk,
                  a,
                  b,
                  d,
                  tot: a + b + d,
                  label: MONTHS_SHORT[Number(mmn) - 1] || mmn,
                };
              });
              const axisMax = niceCeil(Math.max(...bars.map((x) => x.tot), 1));
              // Segments empilés du HAUT vers le BAS (flex column, 1er = haut).
              const SEG = [
                ["d", DS.srcDirect],
                ["b", DS.srcBooking],
                ["a", DS.srcAirbnb],
              ];
              const H = 200;
              const FRACS = [1, 0.8, 0.6, 0.4, 0.2, 0];
              const LEGEND_BAR = [
                [DS.srcAirbnb, "Airbnb"],
                [DS.srcBooking, "Booking"],
                [DS.srcDirect, "Direct"],
              ];
              return (
                <div
                  className="fbz-astat-card"
                  style={{
                    background: DS.white,
                    border: `0.5px solid ${DS.border}`,
                    borderRadius: 14,
                    padding: 18,
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 10,
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <span
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 8,
                          background: CAT.violet.bg,
                          color: CAT.violet.fg,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <BarChart3 size={16} strokeWidth={2} />
                      </span>
                      <span
                        style={{
                          fontFamily: DS.fontDisplay,
                          fontWeight: 600,
                          fontSize: 17,
                          color: DS.marineDark,
                        }}
                      >
                        Évolution du chiffre d'affaires
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 14,
                        fontSize: 12,
                        color: DS.textSecondary,
                        fontFamily: DS.fontBody,
                      }}
                    >
                      {LEGEND_BAR.map(([col, lbl]) => (
                        <span key={lbl} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 9, height: 9, borderRadius: 2, background: col }} />
                          {lbl}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="fbz-table-wrap" style={{ overflowX: "auto" }}>
                    <div style={{ minWidth: Math.max(bars.length * 34, 260) }}>
                      <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                        {/* Axe Y */}
                        <div style={{ position: "relative", width: 44, height: H, flexShrink: 0 }}>
                          {FRACS.map((f) => (
                            <span
                              key={f}
                              style={{
                                position: "absolute",
                                left: 0,
                                top: `${(1 - f) * 100}%`,
                                transform: "translateY(-50%)",
                                fontSize: 10.5,
                                color: "#b6bcc6",
                                fontFamily: DS.fontBody,
                              }}
                            >
                              {kEur(axisMax * f)}
                            </span>
                          ))}
                        </div>
                        {/* Zone barres + gridlines */}
                        <div style={{ position: "relative", flex: 1, height: H }}>
                          {FRACS.map((f) => (
                            <div
                              key={f}
                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: `${(1 - f) * 100}%`,
                                borderTop: f === 0 ? "1px solid #dde1e6" : "1px dashed #e8ebef",
                              }}
                            />
                          ))}
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", gap: 8 }}>
                            {bars.map((bar) => {
                              const topKey = SEG.find(([k]) => bar[k] > 0)?.[0];
                              return (
                                <div
                                  key={bar.mk}
                                  title={`${bar.label} · ${fmtE(bar.tot)}`}
                                  style={{
                                    flex: 1,
                                    minWidth: 14,
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  {SEG.map(([k, col]) => {
                                    const h = bar.tot > 0 ? (bar[k] / axisMax) * 100 : 0;
                                    if (h <= 0) return null;
                                    return (
                                      <div
                                        key={k}
                                        style={{
                                          height: `${h}%`,
                                          background: col,
                                          borderRadius: k === topKey ? "3px 3px 0 0" : 0,
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      {/* Labels mois (alignés sous les barres, offset = axe + gap). */}
                      <div style={{ display: "flex", gap: 8, marginLeft: 56, marginTop: 7 }}>
                        {bars.map((bar, i) => (
                          <div
                            key={bar.mk}
                            style={{
                              flex: 1,
                              minWidth: 14,
                              textAlign: "center",
                              fontSize: 11,
                              color: i === bars.length - 1 ? DS.marineDark : DS.textMuted,
                              fontWeight: i === bars.length - 1 ? 600 : 400,
                            }}
                          >
                            {bar.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ── DÉTAIL DE L'ACTIVITÉ : tuiles. ── */}
          <SubLabel>Détail de l'activité</SubLabel>
          <div className="fbz-astat-tiles">
            {[
              { icon: Building2, cat: CAT.sky, label: "Hébergements actifs", value: fmt(kpis.hebergementsActifs), info: INFO.hebergementsActifs },
              { icon: Moon, cat: CAT.sky, label: "Nuits louées", value: fmt(total.nuitsLouees), info: INFO.nuitsLouees },
              { icon: CalendarOff, cat: CAT.sky, label: "Nuits restantes", value: fmt(total.nuitsRestantes), info: INFO.nuitsRestantes },
              { icon: Users, cat: CAT.violet, label: "Voyageurs / séjour", value: fmt(kpis.nbVoyageursMoy), info: INFO.nbVoyageursMoy },
              { icon: CalendarDays, cat: CAT.violet, label: "Nuits / séjour", value: fmt(kpis.nuitsMoyResa), info: INFO.nuitsMoyResa },
              { icon: Tag, cat: CAT.violet, label: "Prix moyen / séjour", value: fmtEur(kpis.prixMoyenResa), info: INFO.prixMoyenResa },
              { icon: Percent, cat: CAT.amber, label: "Commissions plateformes", value: fmtEur(total.commissionsOta), info: INFO.commissionsOta },
              { icon: ReceiptText, cat: CAT.amber, label: "Taxe de séjour", value: fmtEur(total.taxe), info: INFO.taxe },
              { icon: Sparkles, cat: CAT.amber, label: "Ménage", value: fmtEur(total.menage), info: INFO.menage },
              { icon: KeyRound, cat: CAT.green, label: "Check-in / out", value: `${fmt(kpis.nbCheckin)} / ${fmt(kpis.nbCheckout)}`, info: INFO.checkinCheckout },
            ].map((t) => (
              <div
                key={t.label}
                className="fbz-astat-card"
                style={{
                  background: DS.white,
                  border: `0.5px solid #eceef1`,
                  borderRadius: 12,
                  padding: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: t.cat.bg,
                    color: t.cat.fg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <t.icon size={19} strokeWidth={1.9} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, color: DS.textSecondary, lineHeight: 1.25 }}>
                    {t.label}
                    {t.info && <InfoDot text={t.info} />}
                  </div>
                  <div
                    style={{
                      fontFamily: DS.fontDisplay,
                      fontWeight: 700,
                      fontSize: 20,
                      color: DS.marineDark,
                      marginTop: 3,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {t.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 18, margin: "12px 2px 22px", fontSize: 11, color: DS.textMuted, flexWrap: "wrap" }}>
            {LEGEND.map(([lbl, col]) => (
              <span key={lbl} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: col }} />
                {lbl}
              </span>
            ))}
          </div>

          {/* ── RÉPARTITION PAR PLATEFORME : 2 donuts. ── */}
          <SubLabel>Répartition par plateforme</SubLabel>
          <div className="fbz-donuts" style={{ marginBottom: 22 }}>
            <DonutCard
              title="Répartition des séjours"
              info={INFO.donutResas}
              parts={donutParts(stats.parPlateforme?.repartitionResas, "count")}
            />
            <DonutCard
              title="Répartition du CA brut"
              info={INFO.donutCa}
              parts={donutParts(stats.parPlateforme?.caBrutParPlateforme, "ca")}
            />
          </div>

          {/* ── DÉTAIL MOIS PAR MOIS : table avec variations. ── */}
          <SubLabel>Détail mois par mois</SubLabel>
          <MonthlyDetailTable entries={parMoisEntries} total={total} />
    </>
  );
}

export default function AdminStatsView({ cfg, dataProjete, blocksByMonth, lectureRatee = false }) {
  // F13 (relecture argent) : données de facturation non lues → chaque modèle
  // affiche « — » (boîte existante) au lieu de chiffres calculés sur un vide ;
  // pas d'export. Les filtres restent utilisables.
  // ── Plage libre « De … à … » (mois calendaires inclus) — défaut ADMIN =
  // 12 derniers mois COMPLETS (mesure 3 : le mois en cours ne dilue plus). ──
  const defRange = useMemo(() => defaultRangeAdmin(), []);
  const [fromMk, setFromMk] = useState(defRange.from);
  const [toMk, setToMk] = useState(defRange.to);

  // ── POINT 4 — filtre logements par cases (maîtresse « Tous » + individuelles). ──
  const [allLogs, setAllLogs] = useState(true); // vue globale par défaut
  const [selectedLids, setSelectedLids] = useState(() => new Set()); // actif quand allLogs=false

  const flat = useMemo(() => getAllLogsFlat(cfg), [cfg]);

  // Périmètre effectif : tous les lids (allLogs) sinon l'ensemble coché.
  const scopeLids = useMemo(() => {
    if (allLogs) return flat.map((l) => l.id);
    return flat.filter((l) => selectedLids.has(l.id)).map((l) => l.id);
  }, [allLogs, selectedLids, flat]);
  const noLogSelected = !allLogs && selectedLids.size === 0;

  // Plage : si invalide (from > to / > 5 ans / champ vide) → on GARDE la dernière
  // plage valide + on affiche un message discret (jamais de recalcul sur invalide).
  const lastValidPeriodRef = useRef(null);
  const { period, rangeError } = useMemo(() => {
    const err = validateRange(fromMk, toMk);
    if (err) {
      const fallback = lastValidPeriodRef.current || enumMonths(defRange.from, defRange.to);
      return { period: fallback, rangeError: err };
    }
    const months = enumMonths(fromMk, toMk);
    lastValidPeriodRef.current = months;
    return { period: months, rangeError: null };
  }, [fromMk, toMk, defRange]);

  // ── SÉPARATION DES DEUX MODÈLES (décision 1, jamais mélangés) ──
  // Conciergerie = le périmètre SANS les chambres sous-loc → modèle commission.
  // Sous-location = les chambres lc* du périmètre → modèle marge.
  const conciergerieLids = useMemo(
    () => scopeLids.filter((id) => !isSousLocLid(id)),
    [scopeLids],
  );
  const sousLocInScope = useMemo(
    () => scopeLids.some((id) => isSousLocLid(id)),
    [scopeLids],
  );

  const stats = useMemo(
    () =>
      aggregateStats({
        ...buildAdminStatsInputs({
          cfg,
          dataProjete,
          blocksByMonth,
          period,
          scopeLids: conciergerieLids,
        }),
        period,
      }),
    [cfg, dataProjete, blocksByMonth, period, conciergerieLids],
  );

  // Modèle sous-location : marge = CA − loyer versé − charges portées, les
  // MÊMES fonctions que Compta (allocSousLocCharges, parts égales 1/N).
  const sousLocStats = useMemo(
    () => buildSousLocStats({ cfg, dataProjete, blocksByMonth, period, scopeLids }),
    [cfg, dataProjete, blocksByMonth, period, scopeLids],
  );
  const showSousLoc = sousLocInScope && sousLocStats.parChambre.length > 0;

  const total = stats?.total || null;
  const parMoisEntries = stats
    ? Object.entries(stats.parMois || {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    : [];
  const hasAnyData = total && (total.nbResa > 0 || total.nuitsLouees > 0);

  // ── Bloc « Par propriétaire » — moteur KV reconstruit à l'identique d'App.jsx. ──
  // BYTE-IDENTIQUE au wrapper calc de l'ancien onglet Annuel (gate au centime).
  const proprioRows = useMemo(() => {
    const scopeSet = new Set(conciergerieLids);
    const calcWrap = (lid, taux, mk) =>
      mergeCalc(
        calcPure(cfg, dataProjete, lid, taux, mk),
        calcDirectInternePure(blocksByMonth, lid, taux, mk),
      );
    const rows = [];
    for (const prop of cfg?.props || []) {
      if (prop.id === "p6") continue; // sous-loc exclue (comme AnnuelTab)
      const logs = (prop.logs || []).filter((l) => scopeSet.has(l.id));
      if (!logs.length) continue;
      let comm = 0;
      let tot = 0;
      let ca = 0;
      let men = 0;
      for (const mk of period) {
        for (const l of logs) {
          const c = calcWrap(l.id, l.taux, mk) || {};
          // Lot A4 (30/10) : taux de commission absent → calc() signale
          // `commManquante` ; la commission et le facturé deviennent inconnus
          // (« — »), jamais 0 € en silence. Un inconnu ne s'additionne pas.
          comm += c.commManquante ? NaN : c.comm || 0;
          tot += c.commManquante ? NaN : c.total || 0;
          ca += c.ca || 0;
          men += c.men || 0;
        }
      }
      if (!(ca || tot || comm || men)) continue; // pas d'activité sur la période → masqué
      rows.push({
        id: prop.id,
        name: propDisplayName(prop) || prop.nom || prop.id,
        ca,
        men,
        // ⚠️ `total` = commission + ménage (sans prestations, ajustements ni
        // remplacements, que la facture inclut). L'écran Facturation affiche
        // désormais le total de la facture (décision Dimitri 2026-10-01,
        // aFacturer dans App.jsx) ; cette colonne pas encore — il lui faudrait
        // les incidents (remplacements) de chaque mois de la période.
        facture: tot,
        comm,
      });
    }
    return rows;
  }, [cfg, dataProjete, blocksByMonth, period, conciergerieLids]);

  // ── Export CSV complet (client-only, lecture seule). ──
  const scopeLabel = () => {
    if (allLogs) return "Tous les logements";
    if (selectedLids.size === 0) return "Aucun logement";
    return flat
      .filter((l) => selectedLids.has(l.id))
      .map((l) => l.id)
      .join(", ");
  };
  const exportCSV = () => {
    try {
      const sep = ";";
      const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
      const num = (n) => (n == null ? "" : fmt2(n).replace(".", ","));
      const rows = [];
      // MIN-2 pessimist : label dérivé de `period` (la plage réellement exportée —
      // les inputs bruts peuvent être invalides pendant rangeError, fallback actif).
      const periodLabel = `De ${formatMois(period[0])} à ${formatMois(period[period.length - 1])}`;
      rows.push([esc("Statistiques FBZ"), esc(periodLabel), esc(scopeLabel())].join(sep));
      rows.push("");
      rows.push(esc("Détail mois par mois"));
      rows.push(
        ["Période", "Nuits louées", "Occupation %", "CA brut", "Commission", "Ménage", "Revenus nets"]
          .map(esc)
          .join(sep),
      );
      for (const [mk, m] of parMoisEntries) {
        rows.push(
          [
            esc(formatMois(mk)),
            m.nuitsLouees ?? "",
            num(m.occupation),
            num(m.caBrut),
            num(m.commissionsOta),
            num(m.menage),
            num(m.revenusNets),
          ].join(sep),
        );
      }
      rows.push(
        [
          esc("Total"),
          total.nuitsLouees ?? "",
          num(total.occupation),
          num(total.caBrut),
          num(total.commissionsOta),
          num(total.menage),
          num(total.revenusNets),
        ].join(sep),
      );
      rows.push("");
      rows.push(esc("Par propriétaire"));
      rows.push(["Propriétaire", "CA", "Ménage", "Facturé", "Commission FBZ"].map(esc).join(sep));
      for (const r of proprioRows) {
        rows.push([esc(r.name), num(r.ca), num(r.men), num(r.facture), num(r.comm)].join(sep));
      }
      // ── Sous-location (modèle marge — jamais mélangée aux chiffres du haut). ──
      if (showSousLoc) {
        rows.push("");
        rows.push(esc("Sous-location (marge = revenus − loyer versé − charges portées)"));
        rows.push(
          ["Période", "Revenus chambres", "Loyer versé", "Charges portées", "Marge"].map(esc).join(sep),
        );
        for (const mk of period) {
          const m = sousLocStats.parMois[mk];
          if (!m) continue;
          rows.push(
            [esc(formatMois(mk)), num(m.ca), num(m.loyer), num(m.charges), num(m.marge)].join(sep),
          );
        }
        const st = sousLocStats.total;
        rows.push([esc("Total"), num(st.ca), num(st.loyer), num(st.charges), num(st.marge)].join(sep));
        rows.push(
          ["Par chambre", "Revenus", "Part de loyer", "Part de charges", "Marge"].map(esc).join(sep),
        );
        for (const c of sousLocStats.parChambre) {
          rows.push(
            [esc(`${c.id} · ${c.nom}`), num(c.ca), num(c.loyer), num(c.charges), num(c.marge)].join(sep),
          );
        }
      }
      const csv = "﻿" + rows.join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statistiques-fbz-${period[0]}_${period[period.length - 1]}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[AdminStatsView] export CSV échoué", e);
    }
  };

  return (
    <div>
      <style>{STATS_CSS}</style>
      <style>{LOCAL_CSS}</style>
      <SectionTitle
        icon={LineChart}
        sub="Deux calculs séparés : la conciergerie (commission) et la sous-location (marge) · lecture seule."
      >
        Statistiques
      </SectionTitle>

      {/* Filtres : plage libre « De … à … » + filtre logements par cases. */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <label
              htmlFor="fbz-astat-from"
              style={{ fontSize: 12.5, color: DS.textSecondary, fontFamily: DS.fontBody }}
            >
              De
            </label>
            <input
              id="fbz-astat-from"
              type="month"
              aria-label="Mois de début"
              value={fromMk}
              max={toMk || undefined}
              onChange={(e) => setFromMk(e.target.value)}
              style={RANGE_INPUT_STYLE}
            />
            <label
              htmlFor="fbz-astat-to"
              style={{ fontSize: 12.5, color: DS.textSecondary, fontFamily: DS.fontBody }}
            >
              à
            </label>
            <input
              id="fbz-astat-to"
              type="month"
              aria-label="Mois de fin"
              value={toMk}
              min={fromMk || undefined}
              onChange={(e) => setToMk(e.target.value)}
              style={RANGE_INPUT_STYLE}
            />
          </div>
          {rangeError && (
            <div
              role="alert"
              style={{ fontSize: 11.5, color: DS.danger, marginTop: 6, fontFamily: DS.fontBody }}
            >
              {rangeError}
            </div>
          )}
        </div>

        <div style={{ marginLeft: "auto" }}>
          <LogementFilter
            flat={flat}
            allLogs={allLogs}
            selectedLids={selectedLids}
            onToggleAll={(v) => {
              setAllLogs(v);
              if (!v) setSelectedLids(new Set()); // décocher « Tous » → tout décoché
            }}
            onToggleLid={(id) => {
              setSelectedLids((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
            }}
          />
        </div>
      </div>

      {noLogSelected && <EmptyBox>Aucun logement sélectionné.</EmptyBox>}

      {/* ══ MODÈLE 1 — CONCIERGERIE (commission), sans les chambres sous-loc. ══ */}
      {!noLogSelected && conciergerieLids.length > 0 && (
        <ModelHeader
          title="Conciergerie"
          aide="Les logements des propriétaires : FBZ facture une commission sur les réservations. Les chambres de la sous-location ne sont pas comptées ici — elles ont leur propre calcul, plus bas. Un logement compte dans les nuits disponibles à partir de son premier mois d'activité."
        />
      )}
      {!noLogSelected && conciergerieLids.length > 0 && lectureRatee && (
        <EmptyBox>—</EmptyBox>
      )}
      {!noLogSelected && conciergerieLids.length > 0 && !lectureRatee && !hasAnyData && (
        <EmptyBox>Aucune activité sur la période sélectionnée.</EmptyBox>
      )}

      {!noLogSelected && !lectureRatee && hasAnyData && (
        <>
          <StatsDashboard stats={stats} />

          {/* ── PAR PROPRIÉTAIRE (absorbé de l'ancien Annuel). ── */}
          {proprioRows.length > 0 && (
            <div
              style={{
                background: DS.white,
                border: `0.5px solid ${DS.border}`,
                borderRadius: 14,
                padding: 18,
                marginTop: 22,
                marginBottom: 20,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 6 }}>
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: CAT.corail.bg,
                    color: CAT.corail.fg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <UsersRound size={16} strokeWidth={2} />
                </span>
                <span style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 17, color: DS.marineDark }}>
                  Par propriétaire
                </span>
                <InfoDot text={INFO.proprio} />
              </div>
              {proprioRows.map((r, i) => (
                <div
                  key={r.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 0",
                    borderTop: i === 0 ? "none" : `0.5px solid #f1f1f3`,
                  }}
                >
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: DS.ciel,
                      color: "#2e3d4d",
                      fontFamily: DS.fontDisplay,
                      fontWeight: 600,
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {initials(r.name)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 15, color: DS.marineDark }}>
                      {r.name}
                    </div>
                    <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 2 }}>
                      CA {fmtE(r.ca)} · ménage {fmtE(r.men)} · facturé {fmtE(r.facture)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: ".04em",
                        textTransform: "uppercase",
                        color: DS.textMuted,
                      }}
                    >
                      Commission FBZ
                    </div>
                    <div
                      style={{
                        fontFamily: DS.fontDisplay,
                        fontWeight: 700,
                        fontSize: 19,
                        color: DS.marine,
                        marginTop: 4,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmtE(r.comm)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </>
      )}

      {/* ══ MODÈLE 2 — SOUS-LOCATION (marge = CA − loyer − charges). ══ */}
      {!noLogSelected && showSousLoc && (
        <>
          <ModelHeader
            title="Sous-location — Maison Zen"
            aide="FBZ loue la maison, paie le loyer et les charges, et encaisse les nuits des chambres. La marge = revenus des chambres − loyer versé − charges portées, avec le loyer et les charges saisis mois par mois dans Facturation. Chaque chambre porte une part égale du loyer et des charges."
          />
          {sousLocStats.selectionPartielle && (
            <div
              style={{
                fontSize: 12,
                color: DS.textSecondary,
                fontFamily: DS.fontBody,
                margin: "0 2px 12px",
              }}
            >
              Sélection partielle : seules les parts des chambres cochées sont
              comptées — ce n'est pas la marge de toute la maison.
            </div>
          )}
          {lectureRatee && <EmptyBox>—</EmptyBox>}
          {!lectureRatee && !sousLocStats.hasActivity && (
            <EmptyBox>
              Rien de saisi sur la période : ni revenus de chambre, ni loyer, ni
              charges.
            </EmptyBox>
          )}
          {!lectureRatee && sousLocStats.hasActivity && (
            <SousLocSection sl={sousLocStats} period={period} />
          )}
        </>
      )}

      {/* ── Export CSV complet (les deux modèles, sections séparées). ── */}
      {!noLogSelected && !lectureRatee && (hasAnyData || (showSousLoc && sousLocStats.hasActivity)) && (
        <button
          type="button"
          onClick={exportCSV}
          className="fbz-press"
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 9,
            padding: 14,
            marginTop: 22,
            background: DS.corail,
            color: "#fff",
            border: "none",
            borderRadius: 12,
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          <Download size={17} strokeWidth={2} /> Exporter CSV complet
        </button>
      )}
    </div>
  );
}

// ── En-tête de modèle (lot Statistiques) : titre + phrase d'aide en clair. ──
// Les deux modèles économiques ont chacun leur section, jamais un chiffre commun.
function ModelHeader({ title, aide }) {
  return (
    <div style={{ margin: "6px 0 14px" }}>
      <div
        style={{
          fontFamily: DS.fontDisplay,
          fontWeight: 600,
          fontSize: 18,
          color: DS.marineDark,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: DS.textSecondary,
          fontFamily: DS.fontBody,
          marginTop: 4,
          maxWidth: 680,
          lineHeight: 1.5,
        }}
      >
        {aide}
      </div>
    </div>
  );
}

// ── Section Sous-location : 4 tuiles + détail mois par mois + par chambre. ──
// Rendu PUR depuis buildSousLocStats (les mêmes fonctions que Compta —
// allocSousLocCharges / calcSousLocForLog, parts égales 1/N par chambre).
function SousLocSection({ sl, period }) {
  const margeColor = (v) => (v < 0 ? DS.danger : DS.marineDark);
  const tiles = [
    { label: "Revenus des chambres", value: fmtEur(sl.total.ca), info: INFO.slCa },
    { label: "Loyer versé", value: fmtEur(sl.total.loyer), info: INFO.slLoyer },
    { label: "Charges portées", value: fmtEur(sl.total.charges), info: INFO.slCharges },
    { label: "Marge", value: fmtEur(sl.total.marge), info: INFO.slMarge, color: margeColor(sl.total.marge) },
  ];
  const monthRows = period
    .filter((mk) => sl.parMois[mk])
    .map((mk) => [mk, sl.parMois[mk]]);
  const th = {
    textAlign: "right",
    padding: "9px 8px",
    fontSize: 10.5,
    color: DS.textMuted,
    fontWeight: 600,
    letterSpacing: ".03em",
    borderBottom: `0.5px solid #eceef1`,
    whiteSpace: "nowrap",
  };
  const thL = { ...th, textAlign: "left" };
  const td = {
    textAlign: "right",
    padding: "10px 8px",
    fontSize: 12.5,
    color: "#3a4a5c",
    borderBottom: `0.5px solid #f4f5f7`,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  };
  const tdL = { ...td, textAlign: "left", fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark };
  return (
    <div data-testid="stats-sousloc">
      {/* 4 tuiles totaux de la période. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {tiles.map((t) => (
          <div
            key={t.label}
            className="fbz-astat-card"
            style={{
              background: DS.white,
              border: `0.5px solid ${DS.border}`,
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <div style={{ fontSize: 11.5, color: DS.textSecondary, lineHeight: 1.25 }}>
              {t.label}
              {t.info && <InfoDot text={t.info} />}
            </div>
            <div
              style={{
                fontFamily: DS.fontDisplay,
                fontWeight: 700,
                fontSize: 21,
                color: t.color || DS.marineDark,
                marginTop: 5,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {t.value}
            </div>
          </div>
        ))}
      </div>

      {/* Détail mois par mois. */}
      <div
        style={{
          background: DS.white,
          border: `0.5px solid ${DS.border}`,
          borderRadius: 14,
          padding: 18,
          marginBottom: 16,
        }}
      >
        <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 16, color: DS.marineDark, marginBottom: 10 }}>
          Mois par mois
        </div>
        <div className="fbz-table-wrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
            <thead>
              <tr>
                <th style={thL}>Période</th>
                <th style={th}>Revenus</th>
                <th style={th}>Loyer versé</th>
                <th style={th}>Charges</th>
                <th style={th}>Marge</th>
              </tr>
            </thead>
            <tbody>
              {monthRows.map(([mk, m]) => (
                <tr key={mk}>
                  <td style={tdL}>{formatMois(mk)}</td>
                  <td style={td}>{fmtEur(m.ca)}</td>
                  <td style={td}>{fmtEur(m.loyer)}</td>
                  <td style={td}>{fmtEur(m.charges)}</td>
                  <td style={{ ...td, fontWeight: 600, color: margeColor(m.marge) }}>{fmtEur(m.marge)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                {[
                  "Total",
                  fmtEur(sl.total.ca),
                  fmtEur(sl.total.loyer),
                  fmtEur(sl.total.charges),
                  fmtEur(sl.total.marge),
                ].map((v, i) => (
                  <td
                    key={i}
                    style={{
                      ...(i === 0 ? tdL : td),
                      borderTop: "1.5px solid #dde1e6",
                      borderBottom: "none",
                      paddingTop: 13,
                      fontWeight: 600,
                      color: i === 4 ? margeColor(sl.total.marge) : DS.marineDark,
                      fontSize: 13,
                    }}
                  >
                    {v}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Par chambre (part égale de loyer et de charges chacune). */}
      <div
        style={{
          background: DS.white,
          border: `0.5px solid ${DS.border}`,
          borderRadius: 14,
          padding: 18,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 16, color: DS.marineDark }}>
            Par chambre
          </span>
          <InfoDot text={INFO.slChambre} />
        </div>
        {sl.parChambre.map((c, i) => (
          <div
            key={c.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "13px 0",
              borderTop: i === 0 ? "none" : `0.5px solid #f1f1f3`,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 14.5, color: DS.marineDark }}>
                {c.nom}
              </div>
              <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 2 }}>
                revenus {fmtE(c.ca)} · part de loyer {fmtE(c.loyer)} · part de charges {fmtE(c.charges)}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  color: DS.textMuted,
                }}
              >
                Marge
              </div>
              <div
                style={{
                  fontFamily: DS.fontDisplay,
                  fontWeight: 700,
                  fontSize: 18,
                  color: margeColor(c.marge),
                  marginTop: 3,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtE(c.marge)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Table mois par mois (locale — colonnes maquette + variations ▲/▼). ──
function DeltaBadge({ cur, prev }) {
  if (prev == null || prev <= 0) return null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (pct === 0) return null;
  const up = pct > 0;
  return (
    <span
      style={{
        display: "block",
        marginTop: 2,
        fontSize: 10,
        fontWeight: 600,
        color: up ? DS.srcDirect : DS.danger,
      }}
    >
      {up ? "▲" : "▼"}
      {Math.abs(pct)}%
    </span>
  );
}

function MonthlyDetailTable({ entries, total }) {
  const th = {
    textAlign: "right",
    padding: "9px 8px",
    fontSize: 10.5,
    color: DS.textMuted,
    fontWeight: 600,
    letterSpacing: ".03em",
    borderBottom: `0.5px solid #eceef1`,
    whiteSpace: "nowrap",
  };
  const thL = { ...th, textAlign: "left" };
  const td = {
    textAlign: "right",
    padding: "10px 8px",
    fontSize: 12.5,
    color: "#3a4a5c",
    borderBottom: `0.5px solid #f4f5f7`,
    verticalAlign: "top",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  };
  const tdL = { ...td, textAlign: "left", fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark };
  return (
    <div
      style={{
        background: DS.white,
        border: `0.5px solid ${DS.border}`,
        borderRadius: 14,
        padding: 18,
        marginBottom: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 12 }}>
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: "#eef1f4",
            color: DS.textSecondary,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <TableIcon size={16} strokeWidth={2} />
        </span>
        <span style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 17, color: DS.marineDark }}>
          Détail mois par mois
        </span>
      </div>
      <div className="fbz-table-wrap" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
          <thead>
            <tr>
              <th style={thL}>Période</th>
              <th style={th}>Nuits</th>
              <th style={th}>% occ.</th>
              <th style={th}>CA brut</th>
              <th style={th}>Commission</th>
              <th style={th}>Ménage</th>
              <th style={th}>Rev. nets</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([mk, m], idx) => {
              const prev = idx > 0 ? entries[idx - 1][1] : null;
              return (
                <tr key={mk}>
                  <td style={tdL}>{formatMois(mk)}</td>
                  <td style={td}>
                    {fmt(m.nuitsLouees)}
                    <DeltaBadge cur={m.nuitsLouees} prev={prev ? prev.nuitsLouees : null} />
                  </td>
                  <td style={td}>{fmtPct(m.occupation)}</td>
                  <td style={td}>
                    {fmtEur(m.caBrut)}
                    <DeltaBadge cur={m.caBrut} prev={prev ? prev.caBrut : null} />
                  </td>
                  <td style={td}>
                    {fmtEur(m.commissionsOta)}
                    <DeltaBadge cur={m.commissionsOta} prev={prev ? prev.commissionsOta : null} />
                  </td>
                  <td style={td}>
                    {fmtEur(m.menage)}
                    <DeltaBadge cur={m.menage} prev={prev ? prev.menage : null} />
                  </td>
                  <td style={td}>
                    {fmtEur(m.revenusNets)}
                    <DeltaBadge cur={m.revenusNets} prev={prev ? prev.revenusNets : null} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...tdL, borderTop: "1.5px solid #dde1e6", borderBottom: "none", paddingTop: 13 }}>
                Total
              </td>
              {[
                fmt(total.nuitsLouees),
                fmtPct(total.occupation),
                fmtEur(total.caBrut),
                fmtEur(total.commissionsOta),
                fmtEur(total.menage),
                fmtEur(total.revenusNets),
              ].map((v, i) => (
                <td
                  key={i}
                  style={{
                    ...td,
                    borderTop: "1.5px solid #dde1e6",
                    borderBottom: "none",
                    paddingTop: 13,
                    fontWeight: 600,
                    color: DS.marineDark,
                    fontSize: 13,
                  }}
                >
                  {v}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 10 }}>
        Variation vs mois précédent · Total occupation = moyenne de la période.
      </div>
    </div>
  );
}

// ── POINT 4 — Filtre logements : bouton + popover de cases à cocher. ──
// Case maîtresse « Tous les logements » (cochée par défaut) : quand active, les
// cases individuelles sont visibles mais désactivées (toutes cochées visuellement,
// périmètre = tous les lids). La décocher active les cases individuelles, toutes
// décochées au départ. Fermeture au clic extérieur + Escape.
// `montrerId` : l'admin voit « l3 · Le Loft » (il travaille avec les codes) ;
// l'espace PROPRIÉTAIRE passe false — un client ne doit jamais lire un code
// technique (29/09). Sans nom, jamais le code non plus : « Logement sans nom ».
export function LogementFilter({ flat, allLogs, selectedLids, onToggleAll, onToggleLid, montrerId = true }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Groupement visuel par propriétaire (facultatif, ordre de flat préservé).
  const groups = useMemo(() => {
    const byProp = new Map();
    for (const l of flat) {
      const key = l.propId || "?";
      if (!byProp.has(key)) byProp.set(key, { nom: l.propNom || l.propId || "Autres", logs: [] });
      byProp.get(key).logs.push(l);
    }
    return [...byProp.values()];
  }, [flat]);

  // LE BOUTON NOMME, IL NE COMPTE PLUS (repris de #1269, 30/09) : « 1 logement »
  // disait combien, jamais lequel — et c'était le seul repère de périmètre une
  // fois le menu refermé. Un seul coché → son nom (jamais le code côté
  // propriétaire). Au-delà, le compte reste le plus lisible.
  const seul = !allLogs && selectedLids.size === 1 ? flat.find((l) => selectedLids.has(l.id)) : null;
  const seulNom = seul ? (montrerId ? seul.nom || seul.id : seul.nom || "Logement sans nom") : null;
  const triggerLabel = allLogs
    ? "Tous les logements"
    : selectedLids.size === 0
      ? "Aucun logement"
      : seulNom || `${selectedLids.size} logement${selectedLids.size > 1 ? "s" : ""}`;
  const active = !allLogs; // périmètre restreint → bouton en surbrillance

  return (
    <div ref={rootRef} style={{ position: "relative", maxWidth: "100%" }}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title={triggerLabel}
        style={{
          height: 40,
          padding: "0 14px",
          borderRadius: 10,
          border: "0.5px solid #cdd5df",
          background: active ? DS.navy : "#fff",
          color: active ? "#fff" : DS.marineDark,
          fontWeight: 600,
          fontSize: 13,
          cursor: "pointer",
          fontFamily: DS.fontBody,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          whiteSpace: "nowrap",
          // Un NOM peut être long : le bouton se borne et coupe proprement
          // plutôt que de pousser la ligne hors de l'écran (375 px).
          maxWidth: "100%",
        }}
      >
        <Building2 size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
        <span data-testid="logement-filter-label" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {triggerLabel}
        </span>
        <span style={{ opacity: 0.7, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          role="group"
          aria-label="Sélection des logements"
          style={{
            position: "absolute",
            top: 46,
            right: 0,
            zIndex: 40,
            width: 268,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: 360,
            overflowY: "auto",
            background: "#fff",
            border: `0.5px solid ${DS.border}`,
            borderRadius: 12,
            boxShadow: "0 10px 28px rgba(27,58,92,.14)",
            padding: 8,
          }}
        >
          <label style={LOGFILTER_MASTER_STYLE}>
            <input
              type="checkbox"
              checked={allLogs}
              onChange={(e) => onToggleAll(e.target.checked)}
              style={{ width: 16, height: 16, cursor: "pointer" }}
            />
            <span style={{ fontWeight: 700, color: DS.marineDark }}>Tous les logements</span>
          </label>
          <div style={{ borderTop: `0.5px solid #eef1f4`, margin: "4px 0" }} />
          {groups.map((g, gi) => (
            <div key={gi} style={{ marginBottom: gi === groups.length - 1 ? 0 : 6 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  color: DS.textMuted,
                  padding: "4px 8px 2px",
                }}
              >
                {g.nom}
              </div>
              {g.logs.map((l) => {
                const checked = allLogs || selectedLids.has(l.id);
                return (
                  <label
                    key={l.id}
                    style={{
                      ...LOGFILTER_ROW_STYLE,
                      opacity: allLogs ? 0.55 : 1,
                      cursor: allLogs ? "not-allowed" : "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={allLogs}
                      onChange={() => onToggleLid(l.id)}
                      style={{ width: 15, height: 15, cursor: allLogs ? "not-allowed" : "pointer" }}
                    />
                    <span style={{ color: DS.marineDark }}>
                      {montrerId ? `${l.id} · ${l.nom || l.id}` : l.nom || "Logement sans nom"}
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const LOGFILTER_MASTER_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  padding: "8px 8px",
  fontSize: 13,
  fontFamily: DS.fontBody,
  cursor: "pointer",
};
const LOGFILTER_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  padding: "6px 8px",
  fontSize: 12.5,
  fontFamily: DS.fontBody,
  borderRadius: 8,
};
